import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { apiService, AuthUser } from '../services/apiService';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    job_title?: string;
  }) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (data: Record<string, any>) => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  error: null,
  login: async () => false,
  register: async () => false,
  logout: async () => {},
  updateProfile: async () => false,
  refreshUser: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthProvider(): AuthContextValue {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiService.authMe();
      setState({ user: response.user, loading: false, error: null });
    } catch {
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiService.authLogin(email, password);
      setState({ user: result.user, loading: false, error: null });
      return true;
    } catch (e: any) {
      setState({ user: null, loading: false, error: 'Invalid credentials' });
      return false;
    }
  }, []);

  const register = useCallback(async (data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    phone?: string;
    job_title?: string;
  }) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiService.authRegister(data);
      setState({ user: result.user, loading: false, error: null });
      return true;
    } catch (e: any) {
      const msg = e.message || 'Registration failed';
      setState({ user: null, loading: false, error: msg });
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.authLogout();
    } catch {
      // ignore
    }
    setState({ user: null, loading: false, error: null });
  }, []);

  const updateProfile = useCallback(async (data: Record<string, any>) => {
    try {
      const result = await apiService.authUpdateMe(data);
      setState(prev => ({ ...prev, user: result.user }));
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    refreshUser,
  };
}
